import { DateUtils } from 'src/app/utils/date.utils';
import {
  FrequencyType,
  KeyDatesBackendEnum,
} from '../model/enums/product-capture.enums';

import { getTimezonesByCountry } from '../model/consts/country-of-offer-to-timezone.const';
import { RollAs } from '../model/consts/roll-as.const';
import { ProductCaptureData } from '../model/interfaces/product-capture.interface';
import { GetDateObjectFromString } from './get-date-object-from-string';
import { GetPayoffStringToDate } from './get-payoff-string-to-date';
import { GetReferenceEntitiesDisplay } from './get-reference-entities-display';

export function GetProductCaptureFormPatchValue(
  prod: ProductCaptureData,
  isEditing: boolean
): ProductCaptureData {
  const setOffset = (keyName) =>
    prod?.summary?.keyDates[keyName]?.offset && prod?.summary?.keyDates[keyName]?.offset === prod?.summary?.tenor // offset ??
      ? FrequencyType.TENOR
      : prod?.summary?.keyDates[keyName]?.offset;

  const out = {
    ...prod,
    summary: {
      ...GetPayoffStringToDate(prod?.summary),
      productName: prod?.summary?.productName,
      recordStatus: isEditing ? prod?.summary?.recordStatus : 'Draft',
      lifecycleStatus: isEditing ? prod?.summary?.lifecycleStatus : 'Draft',
      isin: isEditing ? prod?.summary?.isin : '',
      referenceEntitiesDisplay: GetReferenceEntitiesDisplay(
        prod?.summary?.referenceEntitiesGroup?.referenceEntities
      ),
      underlyingAssetsGroup: {
        ...prod.summary.underlyingAssetsGroup,
        rollAs: isEditing ? prod?.summary?.underlyingAssetsGroup?.rollAs : prod.summary.underlyingAssetsGroup.rollAs || RollAs.TOGETHER,
      },
      keyDates: {
        issueDate: {
          ...prod?.summary?.keyDates?.issueDate,
          offset: setOffset(KeyDatesBackendEnum.issueDate),
          value: GetDateObjectFromString(
            prod?.summary?.keyDates?.issueDate.value
          ) as unknown as string,
        },
        ...(prod.summary.keyDates.interestStartDate && {
          interestStartDate: {
            ...prod.summary.keyDates.interestStartDate,
            offset: setOffset(KeyDatesBackendEnum.interestStartDate),
            value: GetDateObjectFromString(
              prod.summary.keyDates.interestStartDate.value
            ) as unknown as string,
          },
        }),
        ...(prod.summary.keyDates.interestEndDate && {
          interestEndDate: {
            ...prod.summary.keyDates.interestEndDate,
            offset: setOffset(KeyDatesBackendEnum.interestEndDate),
            value: GetDateObjectFromString(
              prod.summary.keyDates.interestEndDate.value
            ) as unknown as string,
          },
        }),
        redemptionDate: {
          ...prod?.summary?.keyDates?.maturityDate,
          offset: setOffset(KeyDatesBackendEnum.maturityDate),
          value: GetDateObjectFromString(
            prod?.summary?.keyDates?.maturityDate?.value
          ) as unknown as string,
        },
        initialObservationDate: {
          ...prod?.summary?.keyDates?.initialFixingDate,
          offset: setOffset(KeyDatesBackendEnum.initialFixingDate),
          value: GetDateObjectFromString(
            prod?.summary?.keyDates?.initialFixingDate.value
          ) as unknown as string,
        },
        finalObservationDate: {
          ...prod?.summary?.keyDates?.finalFixingDate,
          offset: setOffset(KeyDatesBackendEnum.finalFixingDate),
          value: GetDateObjectFromString(
            prod?.summary?.keyDates?.finalFixingDate.value
          ) as unknown as string,
        },
        tradeDate: {
          ...prod?.summary?.keyDates?.tradeDate,
          offset: setOffset(KeyDatesBackendEnum.tradeDate),
          value: GetDateObjectFromString(
            prod?.summary?.keyDates?.tradeDate.value
          ) as unknown as string,
        },
        dtePmtUnadjFirstT: {
          ...prod?.summary?.keyDates?.dtePmtUnadjFirstT,
          offset: setOffset(KeyDatesBackendEnum.dtePmtUnadjFirstT),
          value: GetDateObjectFromString(
            prod?.summary?.keyDates?.dtePmtUnadjFirstT?.value
          ) as unknown as string,
        },
        dtePmtUnadjLast0: {
          ...prod?.summary?.keyDates?.dtePmtUnadjLast0,
          offset: setOffset(KeyDatesBackendEnum.dtePmtUnadjLast0),
          value: GetDateObjectFromString(
            prod?.summary?.keyDates?.dtePmtUnadjLast0?.value
          ) as unknown as string,
        },
        productFinalisationDate: {
          ...prod?.summary?.keyDates?.productFinalisationDate,
          offset: setOffset(KeyDatesBackendEnum.productFinalisationDate),
          value: GetDateObjectFromString(
            prod?.summary?.keyDates?.productFinalisationDate?.value
          ) as unknown as string,
        },
      },
    },
    payoff: {
      ...prod.payoff,
      participation: {
        ...prod.payoff?.participation,
        upside: {
          ...prod.payoff?.participation?.upside,
          rebateDate: GetDateObjectFromString(
            prod.payoff?.participation?.upside?.rebateDate
          ) as unknown as string,
        }
      }
    },
    sales: {
      ...prod?.sales,
    },
    legalForm: {
      ...prod?.legalForm,
      countriesOfOffer: {
        countriesOfOfferArr:
          prod?.legalForm?.countriesOfOffer.countriesOfOfferArr.map(
            (countriesRow) => {
              countriesRow.subscriptions = countriesRow.subscriptions.map(
                (subRow) => {
                  subRow.subStartDate =
                    DateUtils.convertDateTimeStringToNgbDateTime(
                      subRow.subStartDate as unknown as string
                    );
                  subRow.subEndDate =
                    DateUtils.convertDateTimeStringToNgbDateTime(
                      subRow.subEndDate as unknown as string
                    );
                  subRow.isaEndDate =
                    DateUtils.convertDateTimeStringToNgbDateTime(
                      subRow.isaEndDate as unknown as string
                    );
                  subRow.timezones = getTimezonesByCountry(
                    countriesRow.jurisdictionsLegal
                  );
                  return subRow;
                }
              );
              return countriesRow;
            }
          ),
      },
      productIdentifiers: {
        productIdentifiersArr: isEditing
          ? prod?.legalForm?.productIdentifiers?.productIdentifiersArr?.map(
              ({ codeProdIdentifiers, typeProdIdentifiers }) => ({
                codeProdIdentifiers,
                typeProdIdentifiers,
              })
            ) || []
          : [],
      },
    },
    creditProvision: {
      ...prod?.creditProvision,
      creditPeriodEndDate: GetDateObjectFromString(
        prod?.creditProvision?.creditPeriodEndDate
      ) as unknown as string,
    },
    notesAndTasks: {
      ...prod.notesAndTasks,
    },
  } as ProductCaptureData;

  return out;
}
